// SCR-STAFF-1204 — پارامترها و قواعد بالینی (US-CFG-040، 041): مجموعه‌پارامتر متصل به خدمت با محدوده مطلق و پیشنهادی به ازای وضعیت (فیتزپاتریک)،
// نسخه‌دار (BR-CFG-040)؛ کتابخانه قواعد منع مصرف (بسته و اختصاصی) با فعال یا غیرفعال‌سازی با دلیل فقط برای پزشک دارای مجوز و تأیید TenantOwner برای
// غیرفعال‌سازی قاعده ایمنی Absolute (BR-CFG-041). افزودن یا ویرایش شرط قاعده با فرم CLN (SCR-STAFF-508). محتوای نمونه با نوار «محتوای نمونه».
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ClinicalRuleView,
  type ParameterInfo,
  type ParameterSetView,
  type RuleOverrideView,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  latinDigits,
  ListPage,
  SampleBadge,
  SampleContentBanner,
  StatusChip,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { cfgKeys } from './common';

export const SCR_STAFF_1204 = 'SCR-STAFF-1204';

const FITZPATRICK = ['I', 'II', 'III', 'IV', 'V', 'VI'];
const TYPES = ['Number', 'Integer', 'Choice', 'Text'] as const;

interface ParameterRow {
  key: string;
  name: string;
  unit: string;
  type: (typeof TYPES)[number];
  absoluteMin: string;
  absoluteMax: string;
  recommended: Record<string, { min: string; max: string }>;
}

function text(value: number | string | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function num(value: string): number | null {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(latinDigits(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function toRows(parameters: readonly ParameterInfo[]): ParameterRow[] {
  return parameters.map((parameter) => ({
    key: parameter.key,
    name: parameter.name,
    unit: parameter.unit ?? '',
    type: (parameter.type as ParameterRow['type']) ?? 'Number',
    absoluteMin: text(parameter.absoluteMin),
    absoluteMax: text(parameter.absoluteMax),
    recommended: Object.fromEntries(parameter.recommended.map((range) => [range.condition, { min: text(range.min), max: text(range.max) }])),
  }));
}

function ParameterSetDialog({ set, onClose }: { set: ParameterSetView | null; onClose: () => void }) {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState(set?.code ?? '');
  const [name, setName] = useState(set?.name ?? '');
  const [serviceIds, setServiceIds] = useState<string[]>(set ? [...set.serviceIds] : []);
  const [conditionKind, setConditionKind] = useState(set?.conditionKind ?? 'fitzpatrick');
  const [conditions, setConditions] = useState((set?.conditions ?? FITZPATRICK).join(', '));
  const [rows, setRows] = useState<ParameterRow[]>(set ? toRows(set.parameters) : []);
  const [dirty, setDirty] = useState(false);
  const services = useQuery({
    queryKey: ['catalog', 'services', 'all', session?.tenantId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services', { params: { query: { limit: 200 } } })),
  });
  const conditionList = conditions.split(/[,،\s]+/).map((value) => value.trim()).filter(Boolean);
  const body = {
    code: set ? null : code.trim(),
    name: name.trim(),
    serviceIds,
    deviceTypeId: null,
    conditionKind: conditionKind.trim() || null,
    conditions: conditionList,
    parameters: rows.map((row) => ({
      key: row.key.trim(),
      name: row.name.trim(),
      unit: row.unit.trim() || null,
      type: row.type,
      absoluteMin: num(row.absoluteMin),
      absoluteMax: num(row.absoluteMax),
      options: null,
      recommended: conditionList
        .filter((condition) => row.recommended[condition] && (row.recommended[condition]!.min || row.recommended[condition]!.max))
        .map((condition) => ({ condition, min: num(row.recommended[condition]!.min), max: num(row.recommended[condition]!.max) })),
    })),
    isActive: null,
  };
  const save = useMutation({
    mutationFn: () =>
      set
        ? unwrap(tenantApi.PUT('/api/v1/configuration/parameter-sets/{id}', { params: { path: { id: set.id } }, body: body as never }))
        : unwrap(tenantApi.POST('/api/v1/configuration/parameter-sets', { body: body as never })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cfgKeys.parameterSets(session?.tenantId) });
      onClose();
    },
  });
  const patch = (index: number, change: Partial<ParameterRow>) => {
    setDirty(true);
    setRows((current) => current.map((row, position) => (position === index ? { ...row, ...change } : row)));
  };
  const serviceOptions = services.data?.items ?? [];

  return (
    <FormDialog
      open
      titleKey={set ? 'cfg:parameters.edit' : 'cfg:parameters.create'}
      screenId={SCR_STAFF_1204}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={dirty}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="lg"
    >
      {set?.isSample && <SampleContentBanner />}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField label={t('cfg:parameters.code')} value={code} onChange={(event) => { setDirty(true); setCode(event.target.value); }} disabled={!!set} required slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'parameter-set-code' } }} />
        <TextField label={t('cfg:parameters.name')} value={name} onChange={(event) => { setDirty(true); setName(event.target.value); }} required fullWidth slotProps={{ htmlInput: { 'data-testid': 'parameter-set-name' } }} />
      </Stack>
      <Autocomplete
        multiple
        options={serviceOptions.map((service) => service.id)}
        value={serviceIds}
        getOptionLabel={(id) => serviceOptions.find((service) => service.id === id)?.name ?? id}
        onChange={(_event, value) => { setDirty(true); setServiceIds(value); }}
        renderInput={(params) => <TextField {...params} label={t('cfg:parameters.services')} required />}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField label={t('cfg:parameters.condition_kind')} value={conditionKind} onChange={(event) => { setDirty(true); setConditionKind(event.target.value); }} slotProps={{ htmlInput: { dir: 'ltr' } }} />
        <TextField label={t('cfg:parameters.conditions')} value={conditions} onChange={(event) => { setDirty(true); setConditions(event.target.value); }} fullWidth helperText={t('cfg:parameters.conditions_hint')} />
      </Stack>
      {rows.map((row, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 1.5 }} data-testid={`parameter-row-${index}`}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
            <TextField size="small" label={t('cfg:parameters.key')} value={row.key} onChange={(event) => patch(index, { key: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            <TextField size="small" label={t('cfg:parameters.parameter_name')} value={row.name} onChange={(event) => patch(index, { name: event.target.value })} />
            <TextField size="small" label={t('cfg:parameters.unit')} value={row.unit} onChange={(event) => patch(index, { unit: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} sx={{ width: 110 }} />
            <TextField size="small" select label={t('cfg:parameters.type')} value={row.type} onChange={(event) => patch(index, { type: event.target.value as ParameterRow['type'] })} sx={{ width: 120 }}>
              {TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`cfg:parameters.types.${type}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" label={t('cfg:parameters.absolute_min')} value={row.absoluteMin} onChange={(event) => patch(index, { absoluteMin: event.target.value })} sx={{ width: 110 }} />
            <TextField size="small" label={t('cfg:parameters.absolute_max')} value={row.absoluteMax} onChange={(event) => patch(index, { absoluteMax: event.target.value })} sx={{ width: 110 }} />
            <IconButton aria-label={t('cfg:parameters.remove')} onClick={() => { setDirty(true); setRows((current) => current.filter((_row, position) => position !== index)); }}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
          {conditionList.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
              {conditionList.map((condition) => (
                <Stack key={condition} direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" sx={{ minWidth: 24 }}>
                    <bdi dir="ltr">{condition}</bdi>
                  </Typography>
                  <TextField size="small" placeholder={t('cfg:parameters.min')} value={row.recommended[condition]?.min ?? ''} onChange={(event) => patch(index, { recommended: { ...row.recommended, [condition]: { min: event.target.value, max: row.recommended[condition]?.max ?? '' } } })} sx={{ width: 72 }} />
                  <TextField size="small" placeholder={t('cfg:parameters.max')} value={row.recommended[condition]?.max ?? ''} onChange={(event) => patch(index, { recommended: { ...row.recommended, [condition]: { min: row.recommended[condition]?.min ?? '', max: event.target.value } } })} sx={{ width: 72 }} />
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>
      ))}
      <Button startIcon={<AddIcon />} onClick={() => { setDirty(true); setRows((current) => [...current, { key: '', name: '', unit: '', type: 'Number', absoluteMin: '', absoluteMax: '', recommended: {} }]); }} data-testid="parameter-add">
        {t('cfg:parameters.add_parameter')}
      </Button>
    </FormDialog>
  );
}

function ParameterSetsTab() {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [editing, setEditing] = useState<ParameterSetView | null | 'new'>(null);
  const sets = useQuery({ queryKey: cfgKeys.parameterSets(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/parameter-sets')) });
  const canWrite = access.can('cfg.parameters.manage') && access.accessMode === 'Full';
  const columns: DataTableColumn<ParameterSetView>[] = [
    {
      id: 'name',
      headerKey: 'cfg:parameters.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.name}</span>
          {row.isSample && <SampleBadge />}
        </Stack>
      ),
    },
    { id: 'parameters', headerKey: 'cfg:parameters.parameters', cell: (row) => row.parameters.map((parameter) => parameter.name).join(t('common:list_separator', { defaultValue: ', ' })) },
    { id: 'services', headerKey: 'cfg:parameters.services', cell: (row) => faDigits(row.serviceIds.length) },
    {
      id: 'version',
      headerKey: 'cfg:parameters.version',
      cell: (row) => (
        <span>
          {faDigits(String(row.version))} · <JalaliDate value={row.effectiveFrom} withTime />
        </span>
      ),
    },
    { id: 'state', headerKey: 'cfg:parameters.state', cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} /> },
  ];
  return (
    <Stack spacing={1.5}>
      <DataTable
        columns={columns}
        rows={sets.data?.items}
        getRowId={(row) => row.id}
        isLoading={sets.isPending}
        error={sets.error}
        onRetry={() => void sets.refetch()}
        captionKey="cfg:parameters.title"
        emptyTitleKey="cfg:parameters.empty"
        onRowClick={canWrite ? (row) => setEditing(row) : undefined}
        toolbar={
          canWrite ? (
            <Button startIcon={<AddIcon />} onClick={() => setEditing('new')} data-testid="parameter-set-create">
              {t('cfg:parameters.create')}
            </Button>
          ) : undefined
        }
      />
      {editing && <ParameterSetDialog set={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Stack>
  );
}

function ReasonDialog({ rule, active, onClose }: { rule: ClinicalRuleView; active: boolean; onClose: () => void }) {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const change = useMutation({
    mutationFn: () =>
      unwrap(
        active
          ? tenantApi.POST('/api/v1/configuration/clinical-rules/{ruleId}:activate', { params: { path: { ruleId: rule.id } }, body: { reason } })
          : tenantApi.POST('/api/v1/configuration/clinical-rules/{ruleId}:deactivate', { params: { path: { ruleId: rule.id } }, body: { reason } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cfgKeys.rules(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey={active ? 'cfg:rules.activate' : 'cfg:rules.deactivate'} screenId={SCR_STAFF_1204} onSubmit={() => reason.trim() && change.mutate()} onClose={onClose} isDirty={!!reason} isSubmitting={change.isPending} submitError={change.error}>
      {rule.isSample && <SampleContentBanner />}
      <Typography>
        <bdi dir="ltr">{rule.code}</bdi> — {rule.message}
      </Typography>
      {!active && rule.isSafety && <Alert severity="warning">{t('cfg:rules.safety_hint')}</Alert>}
      <TextField label={t('cfg:rules.reason')} value={reason} onChange={(event) => setReason(event.target.value)} required multiline minRows={2} slotProps={{ htmlInput: { 'data-testid': 'rule-reason' } }} />
    </FormDialog>
  );
}

function RulesTab() {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [changing, setChanging] = useState<{ rule: ClinicalRuleView; active: boolean } | null>(null);
  const rules = useQuery({ queryKey: cfgKeys.rules(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/clinical-rules')) });
  const decide = useMutation({
    mutationFn: ({ pending, approve }: { pending: RuleOverrideView; approve: boolean }) =>
      unwrap(
        approve
          ? tenantApi.POST('/api/v1/configuration/clinical-rules/overrides/{id}:approve', { params: { path: { id: pending.id } } })
          : tenantApi.POST('/api/v1/configuration/clinical-rules/overrides/{id}:reject', { params: { path: { id: pending.id } } }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: cfgKeys.rules(session?.tenantId) }),
  });
  const canWrite = access.accessMode === 'Full';
  const columns: DataTableColumn<ClinicalRuleView>[] = [
    {
      id: 'code',
      headerKey: 'cfg:rules.code',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <bdi dir="ltr">{row.code}</bdi>
          {row.isSample && <SampleBadge />}
        </Stack>
      ),
    },
    { id: 'target', headerKey: 'cfg:rules.target', cell: (row) => row.targetLabel ?? '—' },
    { id: 'message', headerKey: 'cfg:rules.message', cell: (row) => row.message },
    { id: 'level', headerKey: 'cfg:rules.level', cell: (row) => <StatusChip tone={row.level === 'Absolute' ? 'error' : 'pending'} labelKey={`cfg:rules.levels.${row.level}`} /> },
    { id: 'targets', headerKey: 'cfg:rules.targets', cell: (row) => faDigits(Number(row.serviceCount) + Number(row.categoryCount)) },
    {
      id: 'state',
      headerKey: 'cfg:rules.state',
      cell: (row) => (
        <Stack spacing={0.5}>
          <StatusChip tone={row.isActive ? 'active' : 'inactive'} />
          {row.pending && <StatusChip tone="pending" labelKey="cfg:rules.pending_owner" />}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'common:actions.title',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          {row.pending && rules.data?.canApproveSafety ? (
            <>
              <Button size="small" variant="contained" onClick={() => decide.mutate({ pending: row.pending!, approve: true })} data-testid={`rule-approve-${row.code}`}>
                {t('cfg:rules.approve')}
              </Button>
              <Button size="small" onClick={() => decide.mutate({ pending: row.pending!, approve: false })}>
                {t('cfg:rules.reject')}
              </Button>
            </>
          ) : (
            <Button size="small" disabled={!canWrite || !!row.pending} onClick={() => setChanging({ rule: row, active: !row.isActive })} data-testid={`rule-toggle-${row.code}`}>
              {row.isActive ? t('cfg:rules.deactivate') : t('cfg:rules.activate')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];
  return (
    <Stack spacing={1.5}>
      <Alert severity="info" action={<Button component={RouterLink} to="/settings/clinical" size="small">{t('cfg:rules.open_clinical')}</Button>}>
        {t('cfg:rules.hint')}
      </Alert>
      {decide.error ? <ErrorState error={decide.error} /> : null}
      <DataTable columns={columns} rows={rules.data?.items} getRowId={(row) => row.id} isLoading={rules.isPending} error={rules.error} onRetry={() => void rules.refetch()} captionKey="cfg:rules.title" emptyTitleKey="cfg:rules.empty" />
      {changing && <ReasonDialog rule={changing.rule} active={changing.active} onClose={() => setChanging(null)} />}
    </Stack>
  );
}

export function ParametersAndRulesPage() {
  const { t } = useTranslation('cfg');
  const access = useAccess();
  const canParameters = access.can('cfg.parameters.manage');
  const canRules = access.can('cfg.clinical_rules.manage');
  const [tab, setTab] = useState(canParameters ? 'parameters' : 'rules');
  return (
    <ListPage screenId={SCR_STAFF_1204} titleKey="cfg:parameters.page_title" subtitleKey="cfg:parameters.page_subtitle">
      <Tabs value={tab} onChange={(_event, value: string) => setTab(value)} sx={{ mb: 2 }}>
        {canParameters && <Tab value="parameters" label={t('cfg:parameters.title')} data-testid="tab-parameters" />}
        {canRules && <Tab value="rules" label={t('cfg:rules.title')} data-testid="tab-rules" />}
      </Tabs>
      {tab === 'parameters' && canParameters ? <ParameterSetsTab /> : canRules ? <RulesTab /> : null}
    </ListPage>
  );
}
